import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { User, LoginRequest, LoginResponse } from '../models/auth.model';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from 'app/server/services';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private apiUrl = environment.apiUrl + '/api/auth';
	private currentUserSubject = new BehaviorSubject<User | null>(null);
	private currentTokenSubject = new BehaviorSubject<string | null>(null);

	public currentUser$ = this.currentUserSubject.asObservable();
	public currentToken$ = this.currentTokenSubject.asObservable();

	constructor(
		private http: HttpClient,
		private authenticationService: AuthenticationService
	) {
		this.initializeAuthFromStorage();
	}

	private initializeAuthFromStorage(): void {
		const token = localStorage.getItem('auth_token');
		const user = localStorage.getItem('auth_user');

		if (token && user) {
			this.currentTokenSubject.next(token);
			this.currentUserSubject.next(JSON.parse(user));
		}
	}

	login(credentials: LoginRequest): Observable<LoginResponse> {
		return this.http.post<LoginResponse>(`${this.apiUrl}/login`, credentials).pipe(
			tap(response => {
				if (response.token && response.user) {
					this.setAuthData(response.token, response.user);
				}
			})
		);
	}

	logout(): Observable<any> {
		return this.http.post(`${this.apiUrl}/logout`, {}).pipe(
			tap(() => {
				this.clearAuthData();
			})
		);
	}

	me(): Observable<User> {
		return this.http.get<User>(`${this.apiUrl}/me`).pipe(
			map(response => response as User),
			tap(user => {
				const token = this.currentTokenSubject.value;
				if (token) {
					this.setAuthData(token, user);
				}
			})
		);
	}

	refreshToken(): Observable<LoginResponse> {
		return this.http.post<LoginResponse>(`${this.apiUrl}/refresh`, {}).pipe(
			tap(response => {
				if (response.token && response.user) {
					this.setAuthData(response.token, response.user);
				}
			})
		);
	}

	private setAuthData(token: string, user: User): void {
		localStorage.setItem('auth_token', token);
		localStorage.setItem('auth_user', JSON.stringify(user));
		this.currentTokenSubject.next(token);
		this.currentUserSubject.next(user);
	}

	private clearAuthData(): void {
		localStorage.removeItem('auth_token');
		localStorage.removeItem('auth_user');
		this.currentTokenSubject.next(null);
		this.currentUserSubject.next(null);
	}

	// Read the current user directly from AuthenticationService's in-memory BehaviorSubject.
	// No storage access needed — the value is always up to date after login.
	private getAuthUser(): any | null {
		return this.authenticationService.currentUserObj?.value ?? null;
	}

	get currentUserValue(): User | null {
		return this.currentUserSubject.value ?? this.getAuthUser();
	}

	get currentTokenValue(): string | null {
		return this.currentTokenSubject.value ?? sessionStorage.getItem('currentUser');
	}

	isAuthenticated(): boolean {
		return !!this.currentTokenValue && !!this.getAuthUser();
	}

	isSuperAdmin(): boolean {
		const user = this.getAuthUser();
		if (!user?.roles) return false;
		return user.roles.some((role: any) => role.name === 'SuperAdmin');
	}

	hasRole(roleName: string): boolean {
		const user = this.getAuthUser();
		if (!user?.roles) return false;
		return user.roles.some((role: any) => role.name === roleName);
	}

	hasAnyRole(roleNames: string[]): boolean {
		const user = this.getAuthUser();
		if (!user?.roles) return false;
		return user.roles.some((role: any) => roleNames.includes(role.name));
	}

	hasPermission(permissionName: string): boolean {
		const user = this.getAuthUser();
		if (!user?.permissions) return false;
		return user.permissions.some((p: any) => (typeof p === 'string' ? p : p.name) === permissionName);
	}

	hasAnyPermission(permissionNames: string[]): boolean {
		const user = this.getAuthUser();
		if (!user?.permissions) return false;
		return permissionNames.some(name => user.permissions.some((p: any) => (typeof p === 'string' ? p : p.name) === name));
	}
}
